import axios from "axios";
import { captureException } from "@sentry/react";
import { getApiUrl } from "@utils/baseUrl";
import { ERROR_MSG } from "@utils/enum";
import { toast } from "react-toastify";
import { getIntelUrl } from "@utils/intelUrl";
import { bulkSearchApiEndpoints, MATCHED_COMPANIES_FILE_NAME, SAMPLE_FILE_NAME } from "./constants";
import type { BulkMatchResultsResponse, BulkSearchLogsQuery, BulkSearchLogsResponse, BulkUploadResponse, ConfirmBulkMatchPayload, ConfirmBulkMatchResponse, ConsolidatedCompany, ConsolidatedResultsResponse } from "./types";

export type UploadOptions = {
  onProgress?: (loaded: number, total: number) => void;
  cancelToken?: unknown;
};

// Response shapes are inconsistent across endpoints: most put the message at
// the top level, but /upload's failure() helper nests it under errors[0].
type ApiErrorPayload = {
  message?: string;
  errors?: { message?: string }[];
};

export const getApiErrorMessage = (error: unknown, fallback: string): string => {
  const data = (error as { response?: { data?: ApiErrorPayload } })?.response?.data;
  return data?.message || data?.errors?.[0]?.message || fallback;
};

export const uploadBulkCompanyFile = async (file: File, options: UploadOptions = {}): Promise<BulkUploadResponse> => {
  const formData = new FormData();
  formData.append("file", file);

  const { data } = await axios.post(bulkSearchApiEndpoints.upload, formData, {
    cancelToken: options.cancelToken,
    onUploadProgress: (event: ProgressEvent) => {
      options.onProgress?.(event.loaded, event.total);
    },
  } as never);

  return data;
};

export const getBulkMatchResults = async (searchId: string): Promise<BulkMatchResultsResponse> => {
  const { data } = await axios.get(bulkSearchApiEndpoints.results(searchId));
  return data;
};

export const confirmBulkMatch = async (searchId: string, payload: ConfirmBulkMatchPayload): Promise<ConfirmBulkMatchResponse> => {
  const { data } = await axios.post(bulkSearchApiEndpoints.confirm(searchId), payload);
  return data;
};

// Confirmed companies for the post-confirmation screen. The backend derives the
// list from row state (auto-matched at >= 90, plus anything the user resolved),
// so it reflects the confirmation strategy without the client having to say
// which one was used.
export const getConsolidatedCompanies = async (searchId: string): Promise<ConsolidatedResultsResponse> => {
  const { data } = await axios.get(bulkSearchApiEndpoints.consolidated(searchId));
  return data;
};

export const getBulkSearchLogs = async ({ status, sortOrder, from, size }: BulkSearchLogsQuery): Promise<BulkSearchLogsResponse> => {
  const params = { status: status.length ? status.join(",") : undefined, sort_order: sortOrder, from, size };
  const { data } = await axios.get(bulkSearchApiEndpoints.logs, { params });
  return data;
};

export const fetchCompanyProfileUrl = async (companyId: string): Promise<string> => {
  const { data } = await axios.get(getApiUrl("companyProfiles/getSlug"), { params: { companyId } });
  if (data?.status !== "success" || !data?.data?.companySlug) return "";
  return `${getIntelUrl()}${data.data.companySlug}/${data.data.aliasCompanyId}?country=multicountry&tab=I`;
};

export const openCompanyProfile = async (companyId?: string) => {
  if (!companyId) return;
  try {
    const url = await fetchCompanyProfileUrl(companyId);
    if (url) window.open(url, "_blank");
    else toast.error("No company profile found in our system !!!");
  } catch (error) {
    captureException(error);
    toast.error(getApiErrorMessage(error, ERROR_MSG));
  }
};

const triggerBrowserDownload = (blob: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const parseFileNameFromDisposition = (disposition: string | undefined, fallback: string) => {
  const match = disposition?.match(/filename[^;=\n]*=(?:(\\?['"])(.*?)\1|(?:[^\s]+'.*?')?([^;\n]*))/i);
  const parsed = match?.[2] || match?.[3];
  return parsed ? parsed.replace(/["']/g, "").trim() : fallback;
};

// With responseType "blob" an error response arrives as a Blob containing JSON
// rather than a parsed object, so the server's message has to be read back out
// of it before it can be shown to the user.
const readBlobError = async (blob: Blob, fallback: string) => {
  try {
    const parsed = JSON.parse(await blob.text());
    return parsed?.message || fallback;
  } catch {
    return fallback;
  }
};

// The selected rows are POSTed rather than re-fetched by id, so the file
// contains exactly what the user had selected on screen -- after whatever
// search, country filter and sort the grid applied client-side.
export const downloadSelectedCompanies = async (searchId: string, companies: ConsolidatedCompany[]) => {
  try {
    const response = await axios.post(bulkSearchApiEndpoints.download(searchId), { companies }, { responseType: "blob" });
    const fileName = parseFileNameFromDisposition(response.headers?.["content-disposition"], MATCHED_COMPANIES_FILE_NAME);
    triggerBrowserDownload(response.data, fileName);
  } catch (error) {
    const blob = (error as { response?: { data?: unknown } })?.response?.data;
    if (blob instanceof Blob) throw new Error(await readBlobError(blob, ERROR_MSG));
    throw error;
  }
};

export const downloadBulkSampleFile = async () => {
  try {
    const response = await axios.get(bulkSearchApiEndpoints.sample, { responseType: "blob" });
    const fileName = parseFileNameFromDisposition(response.headers?.["content-disposition"], SAMPLE_FILE_NAME);
    triggerBrowserDownload(response.data, fileName);
  } catch (error) {
    const blob = (error as { response?: { data?: unknown } })?.response?.data;
    if (blob instanceof Blob) throw new Error(await readBlobError(blob, ERROR_MSG));
    throw error;
  }
};
